import { useEffect, useRef } from 'react';
import { useDiretrixDocumentData } from '../../../../hooks/common/use-diretrix-document-data.hook';
import {
  buildCaepfInactiveMessage,
  CAEPF_NOT_FOUND_MESSAGE,
  isActiveCaepf,
  resolveCaepfLookupOutcome,
} from '../../../../hooks/common/use-caepf-selection.hook';
import type { DiretrixDocumentLookupState } from '../../types';

type Props = {
  documentNumber: string;
  useIndividualDocument: boolean;
  name: string;
  onNameChange(value: string): void;
  onAverageAgesChange?(ages: {
    averagePartnerAge?: number;
    averageEmployeeAge?: number;
    partnerCount?: number;
    employeeCount?: number;
  }): void;
  disabled?: boolean;
  onStatusChange?(state: DiretrixDocumentLookupState): void;
};

export function DiretrixDocumentWatcher({
  documentNumber,
  useIndividualDocument,
  name,
  onNameChange,
  onAverageAgesChange,
  disabled,
  onStatusChange,
}: Props) {
  const lastAppliedNameRef = useRef('');
  const latestNameRef = useRef(name);
  const onAverageAgesChangeRef = useRef(onAverageAgesChange);
  const onNameChangeRef = useRef(onNameChange);
  const onStatusChangeRef = useRef(onStatusChange);
  const {
    data,
    isFetching,
    isError,
    cleanDocument,
    debouncedDocument,
    isCompleteDocument,
  } = useDiretrixDocumentData({
    documentNumber,
    useIndividualDocument,
    enabled: !disabled,
  });

  useEffect(() => {
    latestNameRef.current = name;
    onAverageAgesChangeRef.current = onAverageAgesChange;
    onNameChangeRef.current = onNameChange;
    onStatusChangeRef.current = onStatusChange;
  }, [name, onAverageAgesChange, onNameChange, onStatusChange]);

  useEffect(() => {
    if (disabled) return;

    if (!cleanDocument || !isCompleteDocument) {
      onStatusChangeRef.current?.({
        cleanDocument,
        status: 'idle',
      });
      return;
    }

    if (cleanDocument !== debouncedDocument) {
      onStatusChangeRef.current?.({
        cleanDocument,
        status: 'loading',
        helperText: 'Consultando dados cadastrais...',
      });
      return;
    }

    if (isFetching) {
      onStatusChangeRef.current?.({
        cleanDocument,
        status: 'loading',
        helperText: 'Consultando dados cadastrais...',
      });
      return;
    }

    if (isError) {
      onStatusChangeRef.current?.({
        cleanDocument,
        status: 'not-found',
        helperText:
          'Documento não encontrado na consulta. Verifique o número informado para continuar.',
      });
      return;
    }

    if (data) {
      const resolvedName =
        data.documentType === 'cnpj' ? data.legalName : data.responsibleName;

      // CPF (11 díg.) sem nenhum CAEPF vinculado: consulta OK, apenas não há
      // CAEPF. Não é `not-found` — o CPF segue válido como TipoDocumento 1.
      if (data.documentType === 'cpfCaepf') {
        const outcome = resolveCaepfLookupOutcome({
          cleanDocument,
          items: data.items,
        });

        if (outcome === 'no-caepf') {
          onStatusChangeRef.current?.({
            cleanDocument,
            status: 'no-caepf',
            helperText: CAEPF_NOT_FOUND_MESSAGE,
          });
          return;
        }

        if (outcome === 'select') {
          onStatusChangeRef.current?.({
            cleanDocument,
            status: 'found',
            resolvedName: resolvedName?.trim(),
            caepfItems: data.items,
          });
          return;
        }

        // CAEPF (14 díg.) com situação cadastral irregular — inclusive o que
        // acabou de ser preenchido automaticamente a partir de um CPF com um
        // único CAEPF. Bloqueia e avisa em vermelho abaixo do campo.
        if (
          outcome === 'direct' &&
          data.registrationStatus &&
          !isActiveCaepf(data.registrationStatus)
        ) {
          onStatusChangeRef.current?.({
            cleanDocument,
            status: 'inactive-caepf',
            resolvedName: resolvedName?.trim(),
            helperText: buildCaepfInactiveMessage(data.registrationStatus),
          });
          return;
        }
      }

      if (!resolvedName?.trim()) {
        onStatusChangeRef.current?.({
          cleanDocument,
          status: 'not-found',
          helperText:
            'Documento não encontrado na consulta. Verifique o número informado para continuar.',
        });
        return;
      }

      onStatusChangeRef.current?.({
        cleanDocument,
        status: 'found',
        resolvedName: resolvedName.trim(),
        primaryActivityCode:
          data.documentType === 'cnpj' ? data.primaryActivityCode : undefined,
      });
      return;
    }

    onStatusChangeRef.current?.({
      cleanDocument,
      status: 'idle',
    });
  }, [
    cleanDocument,
    data,
    debouncedDocument,
    disabled,
    isCompleteDocument,
    isError,
    isFetching,
  ]);

  useEffect(() => {
    lastAppliedNameRef.current = '';
    onAverageAgesChangeRef.current?.({
      averagePartnerAge: undefined,
      averageEmployeeAge: undefined,
      partnerCount: undefined,
      employeeCount: undefined,
    });

    if (latestNameRef.current.trim()) {
      onNameChangeRef.current('');
    }
  }, [documentNumber, useIndividualDocument]);

  useEffect(() => {
    if (!data) return;

    const nextName =
      data.documentType === 'cnpj' ? data.legalName : data.responsibleName;
    const trimmedName = nextName?.trim();

    if (
      trimmedName &&
      name.trim() !== trimmedName &&
      (!name.trim() || name.trim() === lastAppliedNameRef.current)
    ) {
      lastAppliedNameRef.current = trimmedName;
      onNameChangeRef.current(trimmedName);
    }

    if (data.documentType === 'cnpj') {
      onAverageAgesChangeRef.current?.({
        averagePartnerAge: data.averagePartnerAge,
        averageEmployeeAge: data.averageEmployeeAge,
        partnerCount: data.partnerCount,
        employeeCount: data.employeeCount,
      });
    }
  }, [data, name]);

  return null;
}
